// src/components/Navbar.jsx
import { useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";

export default function Navbar() {
  const { cart, getCartItemsCount } = useCart();
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setIsMenuOpen(false);
    navigate("/login");
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex flex-col leading-tight" onClick={closeMenu}>
            <span className="text-3xl font-extrabold text-purple-700 tracking-tight">
              HandMade
            </span>
            <span className="text-xs text-gray-500 -mt-1">crafted with love</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex gap-8 text-gray-700 font-medium items-center">
            <Link className="hover:text-purple-600 transition" to="/">Home</Link>
            <Link className="hover:text-purple-600 transition" to="/products">Products</Link>

            {!isAuthenticated ? (
              <Link className="hover:text-purple-600 transition" to="/login">Login</Link>
            ) : (
              <>
                <Link className="hover:text-purple-600 transition" to="/orders">My Orders</Link>
                <div className="flex items-center gap-4">
                  <span className="text-gray-700">
                    Hi, <span className="font-semibold text-purple-700">{user.name}</span>
                  </span>
                  {user.role === "admin" && (
                    <Link
                      to="/admin"
                      className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700 transition"
                    >
                      Admin
                    </Link>
                  )}
                  <button onClick={handleLogout} className="text-red-600 font-medium hover:text-red-700 transition">
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Mobile Icons: Cart + Hamburger */}
          <div className="flex items-center gap-4 md:hidden">
            {/* Cart Icon */}
            <Link to="/cart" className="relative text-2xl" onClick={closeMenu}>
              🛒
              {cart && getCartItemsCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-purple-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center shadow">
                  {getCartItemsCount()}
                </span>
              )}
            </Link>

            {/* Hamburger Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 focus:outline-none"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>

          {/* Desktop Cart Icon */}
          <div className="hidden md:block relative">
            <Link to="/cart" className="text-2xl hover:text-purple-600 transition">🛒</Link>
            {cart && getCartItemsCount() > 0 && (
              <span className="absolute -top-2 -right-2 bg-purple-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center shadow">
                {getCartItemsCount()}
              </span>
            )}
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t pt-4 space-y-3">
            <Link
              to="/"
              onClick={closeMenu}
              className="block text-gray-700 hover:text-purple-600 transition py-2 font-medium"
            >
              Home
            </Link>
            <Link
              to="/products"
              onClick={closeMenu}
              className="block text-gray-700 hover:text-purple-600 transition py-2 font-medium"
            >
              Products
            </Link>

            {!isAuthenticated ? (
              <Link
                to="/login"
                onClick={closeMenu}
                className="block text-gray-700 hover:text-purple-600 transition py-2 font-medium"
              >
                Login
              </Link>
            ) : (
              <>
                <Link
                  to="/orders"
                  onClick={closeMenu}
                  className="block text-gray-700 hover:text-purple-600 transition py-2 font-medium"
                >
                  My Orders
                </Link>

                <div className="border-t pt-3 mt-3">
                  <p className="text-sm text-gray-600 mb-2">
                    Logged in as:{" "}
                    <span className="font-semibold text-purple-700">{user.name}</span>
                  </p>

                  {user.role === "admin" && (
                    <Link
                      to="/admin"
                      onClick={closeMenu}
                      className="block bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 transition text-center mb-2 font-medium"
                    >
                      Admin Panel
                    </Link>
                  )}

                  <button
                    onClick={handleLogout}
                    className="block w-full text-left text-red-600 hover:text-red-700 transition py-2 font-medium"
                  >
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}